import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, isEmailVerificationRequiredError } from '@/services/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Upload, ArrowLeft, Loader2, Sparkles,
  Download, RefreshCw, Copy, ChevronDown, Info
} from 'lucide-react';
import MainMenu from '@/components/MainMenu';
import {
  getUnsupportedUploadFormatMessage,
  isSupportedUploadFile,
  LEGACY_DOC_GUIDANCE,
  SUPPORTED_UPLOAD_FORMAT_LABEL,
  UPLOAD_ACCEPT,
} from '@/lib/uploadFormats';

// AI Disclosure Component
function AIDisclosure() {
  return (
    <div className="flex items-start gap-2 p-3 bg-accent/10 border border-accent/20 rounded-lg text-sm">
      <Sparkles className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
      <p className="text-muted-foreground">
        <span className="font-medium text-foreground">AI-generated suggestions.</span>{' '}
        Always review and verify before submitting a resume.
      </p>
    </div>
  );
}

function ResumePreview({ resumeData, fallbackText }) {
  if (!resumeData) {
    return (
      <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
        {fallbackText}
      </pre>
    );
  }

  return (
    <div className="text-sm leading-relaxed text-foreground space-y-6">
      <div className="text-center border-b border-border pb-4">
        {resumeData.name && (
          <h1 className="text-2xl font-bold tracking-tight">{resumeData.name}</h1>
        )}

        {Array.isArray(resumeData.contact_lines) && resumeData.contact_lines.length > 0 && (
          <div className="mt-2 space-y-1 text-sm text-muted-foreground">
            {resumeData.contact_lines
              .filter((line) => line && line.trim())
              .map((line, index) => (
                <div key={`contact-${index}`}>{line}</div>
              ))}
          </div>
        )}
      </div>

      {resumeData.professional_summary?.trim() && (
        <section>
          <h2 className="text-base font-semibold uppercase tracking-wide border-b border-border pb-1 mb-3">
            Professional Summary
          </h2>
          <p>{resumeData.professional_summary}</p>
        </section>
      )}

      {Array.isArray(resumeData.core_competencies) && resumeData.core_competencies.some((item) => item?.trim()) && (
        <section>
          <h2 className="text-base font-semibold uppercase tracking-wide border-b border-border pb-1 mb-3">
            Core Competencies
          </h2>
          <div className="flex flex-wrap gap-2">
            {resumeData.core_competencies
              .filter((item) => item && item.trim())
              .map((item, index) => (
                <span
                  key={`competency-${index}`}
                  className="px-2.5 py-1 rounded-md bg-white border border-border text-sm"
                >
                  {item}
                </span>
              ))}
          </div>
        </section>
      )}

      {Array.isArray(resumeData.professional_experience) && resumeData.professional_experience.length > 0 && (
        <section>
          <h2 className="text-base font-semibold uppercase tracking-wide border-b border-border pb-1 mb-3">
            Professional Experience
          </h2>

          <div className="space-y-5">
            {resumeData.professional_experience.map((job, index) => (
              <div key={`job-${index}`} className="space-y-2">
                {job.title?.trim() && (
                  <div className="font-semibold text-[15px]">{job.title}</div>
                )}

                {job.company?.trim() && (
                  <div className="text-muted-foreground">{job.company}</div>
                )}

                {job.date?.trim() && (
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    {job.date}
                  </div>
                )}

                {job.summary?.trim() && (
                  <p>{job.summary}</p>
                )}

                {Array.isArray(job.bullets) && job.bullets.some((bullet) => bullet?.trim()) && (
                  <ul className="list-disc pl-5 space-y-1">
                    {job.bullets
                      .filter((bullet) => bullet && bullet.trim())
                      .map((bullet, bulletIndex) => (
                        <li key={`job-${index}-bullet-${bulletIndex}`}>{bullet}</li>
                      ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {Array.isArray(resumeData.leadership_training_experience) &&
        resumeData.leadership_training_experience.some((item) => item?.trim()) && (
          <section>
            <h2 className="text-base font-semibold uppercase tracking-wide border-b border-border pb-1 mb-3">
              Leadership &amp; Training Experience
            </h2>
            <ul className="list-disc pl-5 space-y-1">
              {resumeData.leadership_training_experience
                .filter((item) => item && item.trim())
                .map((item, index) => (
                  <li key={`leadership-${index}`}>{item}</li>
                ))}
            </ul>
          </section>
        )}

      {Array.isArray(resumeData.process_improvements_achievements) &&
        resumeData.process_improvements_achievements.some((item) => item?.trim()) && (
          <section>
            <h2 className="text-base font-semibold uppercase tracking-wide border-b border-border pb-1 mb-3">
              Process Improvements &amp; Achievements
            </h2>
            <ul className="list-disc pl-5 space-y-1">
              {resumeData.process_improvements_achievements
                .filter((item) => item && item.trim())
                .map((item, index) => (
                  <li key={`process-${index}`}>{item}</li>
                ))}
            </ul>
          </section>
        )}

      {Array.isArray(resumeData.technical_skills) && resumeData.technical_skills.some((item) => item?.trim()) && (
        <section>
          <h2 className="text-base font-semibold uppercase tracking-wide border-b border-border pb-1 mb-3">
            Technical Skills
          </h2>
          <div className="flex flex-wrap gap-2">
            {resumeData.technical_skills
              .filter((item) => item && item.trim())
              .map((item, index) => (
                <span
                  key={`skill-${index}`}
                  className="px-2.5 py-1 rounded-md bg-white border border-border text-sm"
                >
                  {item}
                </span>
              ))}
          </div>
        </section>
      )}

      {Array.isArray(resumeData.education) && resumeData.education.length > 0 && (
        <section>
          <h2 className="text-base font-semibold uppercase tracking-wide border-b border-border pb-1 mb-3">
            Education
          </h2>

          <div className="space-y-4">
            {resumeData.education.map((edu, index) => (
              <div key={`edu-${index}`} className="space-y-1">
                {edu.degree?.trim() && (
                  <div className="font-semibold">{edu.degree}</div>
                )}
                {edu.school?.trim() && (
                  <div>{edu.school}</div>
                )}
                {edu.date?.trim() && (
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    {edu.date}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default function BuilderPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [currentResume, setCurrentResume] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [skills, setSkills] = useState('');
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [generatedResume, setGeneratedResume] = useState('');
  const [generatedResumeData, setGeneratedResumeData] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isSupportedUploadFile(file)) {
      toast.error(getUnsupportedUploadFormatMessage());
      return;
    }

    setUploadingFile(true);
    try {
      const data = await api.uploadResume(file);
      setCurrentResume(data.text);
      toast.success(`Extracted text from ${data.filename}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleGenerate = async () => {
    if (!jobDescription.trim()) {
      toast.error('Please enter the job description');
      return;
    }

    setLoading(true);
    try {
      const skillsList = skills ? skills.split(',').map(s => s.trim()).filter(s => s) : null;

      const data = await api.generateResume(
        jobDescription,
        currentResume || null,
        skillsList,
      );

      setGeneratedResume(data.resume_markdown);
      setGeneratedResumeData(data.resume_data || null);
      toast.success('Resume generated!');
    } catch (error) {
      if (isEmailVerificationRequiredError(error)) {
        toast.error(error.response?.data?.detail || error.message || 'Please verify your email before using this feature.');
        navigate('/dashboard');
      } else if (error.response?.status === 403) {
        toast.error(error.response.data.detail);
        navigate('/pricing');
      } else {
        toast.error(error.response?.data?.detail || 'Generation failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExportResume = async (format) => {
    setExportLoading(true);
    try {
      const data = await api.exportResume(generatedResume, format, generatedResumeData);
      const blob = await api.downloadProtectedFile(`/api/resume/download/${data.download_id}`);

      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);

      setShowExportDropdown(false);
      toast.success(`${format.toUpperCase()} download started`);
    } catch (error) {
      if (isEmailVerificationRequiredError(error)) {
        toast.error(error.response?.data?.detail || error.message || 'Please verify your email before using this feature.');
        navigate('/dashboard');
      } else {
        toast.error(error.response?.data?.detail || error.message || 'Export failed');
      }
    } finally {
      setExportLoading(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-surface">
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <MainMenu />
          <button
            onClick={() => navigate('/dashboard')}
            className="text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold">AI Resume Builder</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {!generatedResume ? (
            <>
              <div className="flex items-start gap-2 p-3 bg-surface border border-border rounded-lg text-sm">
                <Info className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                <p className="text-muted-foreground">
                  Resumes are processed using AI to generate optimized content. Avoid including sensitive personal data.
                </p>
              </div>

              <div className="bg-white border border-border rounded-xl p-6 shadow-soft">
                <h2 className="font-heading text-lg font-semibold mb-4">Target Job Description *</h2>
                <textarea
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  placeholder="Paste the job description you want to optimize for..."
                  className="w-full h-40 p-4 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none font-mono text-sm"
                  data-testid="job-textarea"
                />
              </div>

              <div className="bg-white border border-border rounded-xl p-6 shadow-soft">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="font-heading text-lg font-semibold">Current Resume (Optional)</h2>
                    <p className="text-sm text-muted-foreground">Use as reference for the new resume</p>
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile}
                    className="flex items-center gap-2 text-primary hover:text-primary-hover transition-colors text-sm"
                    data-testid="upload-btn"
                  >
                    {uploadingFile ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    Upload File
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Supported: {SUPPORTED_UPLOAD_FORMAT_LABEL}. {LEGACY_DOC_GUIDANCE}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={UPLOAD_ACCEPT}
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="file-input"
                />
                <textarea
                  value={currentResume}
                  onChange={(e) => setCurrentResume(e.target.value)}
                  placeholder="Paste your current resume text here (optional)..."
                  className="w-full h-32 p-4 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none font-mono text-sm"
                  data-testid="current-resume-textarea"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border border-border rounded-xl p-6 shadow-soft">
                  <h2 className="font-heading text-lg font-semibold mb-4">Key Skills (Optional)</h2>
                  <input
                    type="text"
                    value={skills}
                    onChange={(e) => setSkills(e.target.value)}
                    placeholder="Python, React, AWS, etc."
                    className="w-full h-11 px-4 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    data-testid="skills-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2">Separate with commas</p>
                </div>
              </div>

              <button
                onClick={handleGenerate}
                disabled={loading || !jobDescription.trim()}
                className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-white py-4 rounded-xl font-medium hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="generate-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating with Claude AI...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate Optimized Resume
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <AIDisclosure />

              <div className="bg-white border border-border rounded-xl p-6 shadow-soft">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-lg font-semibold">Your Generated Resume</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCopy(generatedResume)}
                      className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm"
                      data-testid="copy-btn"
                    >
                      <Copy className="w-4 h-4" />
                      Copy
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => setShowExportDropdown(!showExportDropdown)}
                        disabled={exportLoading}
                        className="flex items-center gap-2 text-primary hover:text-primary-hover transition-colors text-sm"
                        data-testid="export-btn"
                      >
                        {exportLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        Export
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      {showExportDropdown && (
                        <div className="absolute right-0 top-full mt-1 bg-white border border-border rounded-lg shadow-medium py-1 z-10 min-w-[120px]">
                          <button
                            onClick={() => handleExportResume('docx')}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-surface transition-colors"
                            data-testid="export-docx"
                          >
                            DOCX (Word)
                          </button>
                          <button
                            onClick={() => handleExportResume('pdf')}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-surface transition-colors"
                            data-testid="export-pdf"
                          >
                            PDF
                          </button>
                          <button
                            onClick={() => handleExportResume('txt')}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-surface transition-colors"
                            data-testid="export-txt"
                          >
                            TXT (Plain)
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="mb-4 text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">Note:</span> If you rename an exported Word file, keep the .docx extension. Your browser may still ask where to save the file.
                </div>

                <div
                  className="max-w-none p-6 bg-surface rounded-lg border border-border overflow-auto max-h-[600px]"
                  data-testid="generated-resume"
                >
                  <ResumePreview
                    resumeData={generatedResumeData}
                    fallbackText={generatedResume}
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setGeneratedResume('');
                    setGeneratedResumeData(null);
                    setShowExportDropdown(false);
                  }}
                  className="flex-1 bg-white border border-border py-3 rounded-xl font-medium hover:bg-surface transition-all flex items-center justify-center gap-2"
                  data-testid="regenerate-btn"
                >
                  <RefreshCw className="w-5 h-5" />
                  Generate New
                </button>
              </div>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}
